import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2, Camera, AlertTriangle, Calendar as CalendarIcon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { STATUS, api, errMsg } from "@/lib/api";
import { loadImage } from "@/lib/images";
import { toast } from "sonner";
import { useBlocker, useNavigate, useLocation } from "react-router-dom";
import { canGoBack, prevPath } from "@/lib/persist";
import { useDirty } from "@/components/SmartLink";
import { isTouch } from "@/lib/device";

export function BackLink({ fallback, label, className = "inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#1F2128]", testId = "back-link" }) {
  const nav = useNavigate();
  // Go back in history when the previous screen is the list we label (instant, cached view); otherwise navigate to it.
  const back = () => (canGoBack() && (prevPath() || "").split("?")[0] === fallback.split("?")[0] ? nav(-1) : nav(fallback));
  return <button type="button" data-testid={testId} className={className} onClick={back}><ArrowLeft className="h-4 w-4" />{label}</button>;
}


export function UnsavedGuard({ when, testId = "unsaved-dialog", title = "Leave without saving?", description = "You have unsaved changes. If you leave this page now, your changes will be lost and nothing will be saved.", extra }) {
  const leaving = useRef(false);
  const nav = useNavigate();
  const loc = useLocation();
  const blocker = useBlocker(({ currentLocation, nextLocation }) => when && !leaving.current && currentLocation.pathname !== nextLocation.pathname);
  const { setDirty } = useDirty();
  useEffect(() => { setDirty(!!when); return () => setDirty(false); }, [when]); // eslint-disable-line
  useEffect(() => { leaving.current = false; }, [loc.pathname]);
  const leave = () => {
    const next = blocker.location;
    leaving.current = true;
    const from = window.location.pathname;
    blocker.proceed?.();
    setTimeout(() => { if (window.location.pathname === from && next) nav(next.pathname + (next.search || ""), { replace: true, state: next.state }); }, 350);
  };
  useEffect(() => {
    if (!when) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [when]);
  return (
    <AlertDialog open={blocker.state === "blocked"} onOpenChange={(o) => !o && blocker.state === "blocked" && blocker.reset()}>
      <AlertDialogContent data-testid={testId} className="rounded-2xl border border-[#F2E8E5] shadow-[0_24px_60px_-20px_rgba(31,33,40,0.35)]">
        <AlertDialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-[#FDF2F5] text-[#8E5276] flex items-center justify-center mb-1"><AlertTriangle className="h-6 w-6" /></div>
          <AlertDialogTitle className="font-serif text-xl text-[#1F2128] text-center">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[#4B5563] text-center">{description}</AlertDialogDescription>
          {extra && <p className="text-sm text-[#6B7280] bg-[#FAF8F6] rounded-lg px-3 py-2 text-center" data-testid={`${testId}-extra`}>{extra}</p>}
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogCancel data-testid={`${testId}-stay`} className="rounded-full h-11 px-6" onClick={() => blocker.reset()}>Stay on page</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-leave`} className="rounded-full h-11 px-6 bg-[#B91C1C] hover:bg-[#991B1B]" onClick={leave}>Leave & discard changes</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DatePicker({ value, onChange, disabled, testId, placeholder = "Pick a date", minToday = true, className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testId} className={`flex h-11 w-full items-center gap-2 rounded-md border border-input bg-white px-3 text-sm text-left disabled:opacity-60 ${!value ? "text-[#9CA3AF]" : ""} ${className}`}>
          <CalendarIcon className="h-4 w-4 text-[#C86D82] shrink-0" />{value ? selected.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-xl" align="start" data-testid={`${testId}-popover`}>
        <Calendar mode="single" selected={selected} defaultMonth={selected} disabled={minToday ? { before: today } : undefined} initialFocus
          onSelect={(d) => { if (!d) return; onChange(d.toLocaleDateString("en-CA")); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

/** Sticky toolbar that slides away when scrolling down and comes right back on any upward scroll (no layout jump). */
export function StickyBar({ children, testId = "sticky-bar" }) {
  const [hidden, setHidden] = useState(false);
  const [stuck, setStuck] = useState(false);
  const ref = useRef(null);
  const sentinel = useRef(null);
  useEffect(() => {
    let last = window.scrollY, ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY, top = (sentinel.current?.getBoundingClientRect().top ?? 0) + y;
        const past = y > top + 8;
        setStuck(past);
        if (!past) setHidden(false);
        else if (y > last + 4) setHidden(true);
        else if (y < last - 2) setHidden(false);
        last = y; ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (<>
    <div ref={sentinel} aria-hidden className="h-0 -mt-6" />
    <div ref={ref} data-testid={testId} data-stuck={stuck} data-hidden={hidden}
      className={`sticky top-14 md:top-0 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-2 -my-2 transition-[transform,opacity,box-shadow] duration-200 ease-out ${stuck ? "bg-[#FAF8F6] border-b border-[#F2E8E5] shadow-[0_6px_18px_-12px_rgba(31,33,40,0.25)]" : ""} ${hidden ? "-translate-y-[120%] opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}>
      {children}
    </div>
  </>);
}


export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-row flex-wrap items-end justify-between gap-3 sm:gap-4 fade-in">
      <div className="min-w-0">
        <h1 className="text-2xl sm:text-3xl font-serif font-semibold tracking-tight text-[#1F2128]">{title}</h1>
        {subtitle && <p className="text-sm text-[#6B7280] mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2 ml-auto">{children}</div>}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testId, autoFocus }) {
  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
      <Input data-testid={testId} value={value} autoFocus={autoFocus && !isTouch()} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="pl-9 pr-9 h-11 rounded-full bg-white border-[#F2E8E5] focus-visible:ring-[#E89CAE]" />
      {value && <button type="button" onClick={() => onChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#1F2128]"><X className="h-4 w-4" /></button>}
    </div>
  );
}

export function useDebounce(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function StatusBadge({ status, className = "" }) {
  const s = STATUS[status] || STATUS.draft;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs leading-none font-semibold border box-border shrink-0 sm:whitespace-nowrap ${className}`}
      style={{ background: s.bg, color: s.text, borderColor: s.border }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.text }} />{s.label}
    </span>
  );
}

export function SeamstressBadge({ seamstress, size = "sm" }) {
  if (!seamstress) return <span data-testid="seamstress-badge-unassigned" className={`inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#D1D5DB] text-[#6B7280] ${size === "sm" ? "text-xs px-2 py-0.5" : "text-sm px-3 py-1"}`}><span className="h-2.5 w-2.5 rounded-full border border-[#9CA3AF]" />Seamstress unassigned</span>;
  return (
    <span data-testid="seamstress-badge" className={`inline-flex items-center gap-1.5 rounded-full font-medium border ${size === "sm" ? "text-xs px-2 py-0.5" : "text-sm px-3 py-1"}`}
      style={{ borderColor: seamstress.color, color: "#1F2128", background: `${seamstress.color}22` }}>
      <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: seamstress.color }} />{seamstress.name}
    </span>
  );
}

export function GownIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M9 2.5c0 1.6.8 2.8 3 3.2 2.2-.4 3-1.6 3-3.2" />
      <path d="M9 2.5 8 9.5h8l-1-7" />
      <path d="M8 9.5C6.2 12.2 4.5 16.5 3.5 21.5c2.6-.8 5.4-1.2 8.5-1.2s5.9.4 8.5 1.2c-1-5-2.7-9.3-4.5-12" />
      <path d="M8 9.5c1.2.8 2.6 1.2 4 1.2s2.8-.4 4-1.2" />
    </svg>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmText = "Confirm", destructive, urgent, warning, onConfirm, testId = "confirm-dialog", children, cancelText = "Cancel", hideCancel, confirmDisabled }) {
  const [busy, setBusy] = useState(false);
  // One calm, consistent warning look for every confirmation (urgent/warning/destructive only affect the confirm button colour).
  const danger = destructive || urgent;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId} className="rounded-2xl max-h-[90vh] overflow-y-auto border border-[#F2E8E5] shadow-[0_24px_60px_-20px_rgba(31,33,40,0.35)]">
        <div className="space-y-4">
        <div className="flex justify-center" data-testid={`${testId}-${urgent ? "urgent" : "warning"}-icon`}><div className="h-12 w-12 rounded-full flex items-center justify-center bg-[#FDF2F5] text-[#8E5276]"><AlertTriangle className="h-6 w-6" /></div></div>
        <AlertDialogHeader><AlertDialogTitle className="font-serif text-xl text-center text-[#1F2128]">{title}</AlertDialogTitle>{description && <AlertDialogDescription className="text-[#4B5563] text-center">{description}</AlertDialogDescription>}</AlertDialogHeader>
        {children}
        <AlertDialogFooter className="sm:justify-center">
          {!hideCancel && <AlertDialogCancel data-testid={`${testId}-cancel`} className="rounded-full">{cancelText}</AlertDialogCancel>}
          <AlertDialogAction data-testid={`${testId}-confirm`} disabled={busy || confirmDisabled} className={`rounded-full disabled:opacity-40 disabled:cursor-not-allowed ${danger ? "bg-[#B91C1C] hover:bg-[#991B1B]" : "bg-[#C86D82] hover:bg-[#B45C71]"}`}
            onClick={async (e) => { e.preventDefault(); setBusy(true); try { await onConfirm(); onOpenChange(false); } finally { setBusy(false); } }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Empty({ title, hint, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="text-center py-16 text-[#6B7280]">
      <div className="mx-auto h-12 w-12 rounded-full bg-[#FDF2F5] flex items-center justify-center mb-3"><Search className="h-5 w-5 text-[#E89CAE]" /></div>
      <p className="font-medium text-[#1F2128]">{title}</p>{hint && <p className="text-sm mt-1">{hint}</p>}
    </div>
  );
}

export function Spinner() { return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C86D82]" /></div>; }

export function Field({ label, required, children, hint, as: Tag = "label" }) {
  return (
    <Tag className="block space-y-1.5">
      <span className="text-sm font-medium text-[#374151]">{label}{required && <span className="text-[#C86D82]"> *</span>}</span>
      {children}{hint && <span className="block text-xs text-[#9CA3AF]">{hint}</span>}
    </Tag>
  );
}

export function ChannelPicker({ value, onChange, testId, allowUnset, disabled }) {
  const v = value || null;
  const has = (c) => Array.isArray(v) && v.includes(c);
  const toggle = (c) => {
    if (c === "none") return onChange(allowUnset && Array.isArray(v) && v.length === 0 ? null : []);
    const cur = Array.isArray(v) ? v : [];
    onChange(has(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  };
  const opts = [["sms", "SMS"], ["whatsapp", "WhatsApp"], ["none", "None"]];
  return (
    <div className="flex gap-2" data-testid={testId}>
      {opts.map(([k, l]) => { const on = k === "none" ? Array.isArray(v) && v.length === 0 : has(k); return (
        <button key={k} type="button" disabled={disabled} data-testid={`${testId}-${k}`} onClick={() => toggle(k)}
          className={`tap flex-1 rounded-full border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${on ? (k === "none" ? "bg-[#6B7280] text-white border-[#6B7280]" : "bg-[#C86D82] text-white border-[#C86D82]") : "bg-white border-[#E5E7EB] hover:border-[#E89CAE]"}`}>{l}</button>); })}
    </div>
  );
}

export function YesNo({ value, onChange, testId, allowUnset }) {
  return (
    <div className="flex gap-2" data-testid={testId}>
      {[["Yes", true], ["No", false]].map(([l, v]) => (
        <button key={l} type="button" data-testid={`${testId}-${l.toLowerCase()}`} onClick={() => onChange(allowUnset && value === v ? null : v)}
          className={`tap flex-1 rounded-full border text-sm font-medium transition-colors ${value === v ? "bg-[#C86D82] text-white border-[#C86D82]" : "bg-white border-[#E5E7EB] hover:border-[#E89CAE]"}`}>{l}</button>
      ))}
    </div>
  );
}

export function AuthImg({ fileId, className, alt = "", variant = "thumb" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    setSrc(null);
    if (fileId) loadImage(fileId, variant).then((u) => alive && setSrc(u)).catch(() => alive && setSrc("error"));
    return () => { alive = false; };
  }, [fileId, variant]);
  if (!fileId) return <div className={`${className} bg-[#FDF2F5] flex items-center justify-center text-[#E89CAE]`} data-testid="gown-placeholder"><GownIcon className="h-8 w-8" /></div>;
  if (!src || src === "error") return <div className={`${className} bg-[#F7EFEC] ${src ? "" : "animate-pulse"}`} data-testid="image-loading" />;
  return <img src={src} alt={alt} className={className} decoding="async" data-testid="auth-image" />;
}

export function UploadButton({ onUploaded, multiple, label = "Upload photo", testId = "upload-button" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded(data);
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={handle} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" data-testid={testId} disabled={busy} onClick={() => ref.current.click()} className="rounded-full border-[#E89CAE] text-[#8E5276] hover:bg-[#FDF2F5]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}{label}
      </Button>
    </>
  );
}
